import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Check, X, Tags, Package, EyeOff, Eye } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";

const OPT_TYPES = [
  { key: "sizes", label: "Sizes" },
  { key: "surfaces", label: "Surfaces" },
  { key: "units", label: "Units" },
];

export default function CategoryOptions() {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeId, setActiveId] = useState(null);
  const [newCatOpen, setNewCatOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/categories");
      setCats(data);
      setActiveId((cur) => cur && data.some((c) => c.id === cur) ? cur : (data[0]?.id || null));
    } catch (e) { toast.error(apiError(e)); }
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const active = cats.find((c) => c.id === activeId) || null;

  const toggleActive = async (c) => {
    try { await api.put(`/admin/categories/${c.id}`, { active: !c.active }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const deleteCat = async (c) => {
    if (!window.confirm(`Delete the "${c.label}" category?`)) return;
    try { await api.delete(`/admin/categories/${c.id}`); toast.success("Category deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-heading text-2xl font-extrabold flex items-center gap-2"><Tags className="h-6 w-6 text-primary" /> Product Options</h1>
          <p className="text-muted-foreground text-sm font-medium">Create product categories and manage the dropdown options (sizes, surfaces, units) that sellers pick from. New categories start with every existing option — just delete the ones you don't need.</p>
        </div>
        <Button data-testid="new-category-btn" onClick={() => setNewCatOpen(true)} className="font-bold gap-1"><Plus className="h-4 w-4" /> New Category</Button>
      </div>

      {loading ? (
        <div className="text-center text-muted-foreground py-20">Loading…</div>
      ) : (
        <div className="grid lg:grid-cols-[280px_1fr] gap-5">
          <div className="space-y-2">
            {cats.map((c) => (
              <div key={c.id} data-testid={`category-row-${c.value}`}
                className={`rounded-xl border p-3 cursor-pointer transition-colors ${activeId === c.id ? "border-primary bg-primary/5" : "bg-card hover:border-primary/40"}`}
                onClick={() => setActiveId(c.id)}>
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-2 min-w-0">
                    <Package className="h-4 w-4 text-primary shrink-0" />
                    <span className="font-heading font-bold text-sm truncate">{c.label}</span>
                    {!c.active && <Badge variant="secondary" className="text-[10px]">Hidden</Badge>}
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button data-testid={`toggle-category-${c.value}`} onClick={(e) => { e.stopPropagation(); toggleActive(c); }} className="text-muted-foreground hover:text-foreground p-1" title={c.active ? "Hide from marketplace" : "Show on marketplace"}>
                      {c.active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                    </button>
                    <button data-testid={`delete-category-${c.value}`} onClick={(e) => { e.stopPropagation(); deleteCat(c); }} className="text-muted-foreground hover:text-rose-600 p-1"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
                <div className="text-[11px] text-muted-foreground font-medium mt-1 pl-6">{(c.sizes?.length || 0)} sizes · {(c.surfaces?.length || 0)} surfaces · {(c.units?.length || 0)} units</div>
              </div>
            ))}
          </div>

          {active && (
            <div className="space-y-5">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="font-heading font-extrabold text-lg">{active.label}</h2>
                <RenameCategory cat={active} onDone={load} />
              </div>
              {OPT_TYPES.map((t) => (
                <OptionGroup key={t.key} cat={active} type={t.key} label={t.label} onChanged={load} />
              ))}
            </div>
          )}
        </div>
      )}

      <NewCategoryDialog open={newCatOpen} onClose={() => setNewCatOpen(false)} onCreated={(c) => { load(); setActiveId(c.id); }} />
    </div>
  );
}

function RenameCategory({ cat, onDone }) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(cat.label);
  useEffect(() => { setName(cat.label); setEditing(false); }, [cat.id, cat.label]);
  const save = async () => {
    if (!name.trim()) { toast.error("Enter a name"); return; }
    try { await api.put(`/admin/categories/${cat.id}`, { label: name.trim() }); toast.success("Renamed"); setEditing(false); onDone(); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (!editing) return <button data-testid="rename-category-btn" onClick={() => setEditing(true)} className="text-xs font-semibold text-muted-foreground hover:text-foreground inline-flex items-center gap-1"><Pencil className="h-3 w-3" /> Rename</button>;
  return (
    <div className="flex items-center gap-1">
      <Input data-testid="rename-category-input" value={name} onChange={(e) => setName(e.target.value)} className="h-8 w-48" />
      <Button size="sm" data-testid="rename-category-save" onClick={save} className="h-8"><Check className="h-4 w-4" /></Button>
      <Button size="sm" variant="outline" onClick={() => setEditing(false)} className="h-8"><X className="h-4 w-4" /></Button>
    </div>
  );
}

function OptionGroup({ cat, type, label, onChanged }) {
  const [adding, setAdding] = useState("");
  const [editVal, setEditVal] = useState(null); // {old, value}
  const list = cat[type] || [];

  const add = async () => {
    if (!adding.trim()) return;
    try { await api.post(`/admin/categories/${cat.id}/options/add`, { type, value: adding.trim() }); setAdding(""); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (value) => {
    try { await api.post(`/admin/categories/${cat.id}/options/delete`, { type, value }); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const saveEdit = async () => {
    if (!editVal.value.trim()) { toast.error("Enter a value"); return; }
    try { await api.post(`/admin/categories/${cat.id}/options/edit`, { type, old: editVal.old, new: editVal.value.trim() }); setEditVal(null); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="border rounded-2xl p-4 bg-card" data-testid={`option-group-${type}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="font-heading font-bold text-sm">{label}</div>
        <Badge variant="secondary" className="font-bold">{list.length}</Badge>
      </div>
      <div className="flex flex-wrap gap-2">
        {list.map((v) => (
          editVal && editVal.old === v ? (
            <div key={v} className="flex items-center gap-1">
              <Input data-testid={`edit-option-input-${type}`} value={editVal.value} onChange={(e) => setEditVal({ ...editVal, value: e.target.value })} className="h-8 w-32" autoFocus />
              <button data-testid={`edit-option-save-${type}`} onClick={saveEdit} className="text-emerald-600 p-1"><Check className="h-4 w-4" /></button>
              <button onClick={() => setEditVal(null)} className="text-muted-foreground p-1"><X className="h-4 w-4" /></button>
            </div>
          ) : (
            <span key={v} data-testid={`option-chip-${type}-${v}`} className="group inline-flex items-center gap-1.5 bg-secondary rounded-full pl-3 pr-1.5 py-1 text-sm font-semibold">
              {v}
              <button onClick={() => setEditVal({ old: v, value: v })} className="text-muted-foreground hover:text-foreground"><Pencil className="h-3 w-3" /></button>
              <button data-testid={`option-delete-${type}-${v}`} onClick={() => remove(v)} className="text-muted-foreground hover:text-rose-600"><X className="h-3.5 w-3.5" /></button>
            </span>
          )
        ))}
        {list.length === 0 && <span className="text-xs text-muted-foreground font-medium py-1">No options yet.</span>}
      </div>
      <div className="flex items-center gap-2 mt-3">
        <Input data-testid={`add-option-input-${type}`} value={adding} onChange={(e) => setAdding(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), add())} placeholder={`Add ${label.toLowerCase().replace(/s$/, "")}…`} className="h-9 max-w-xs" />
        <Button size="sm" data-testid={`add-option-btn-${type}`} onClick={add} className="font-bold gap-1 h-9"><Plus className="h-4 w-4" /> Add</Button>
      </div>
    </div>
  );
}

function NewCategoryDialog({ open, onClose, onCreated }) {
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) setLabel(""); }, [open]);
  const submit = async () => {
    if (!label.trim()) { toast.error("Enter a category name"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/admin/categories", { label: label.trim() });
      toast.success("Category created with all existing options — trim what you don't need");
      onCreated(data);
      onClose();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-md" data-testid="new-category-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">New Product Category</DialogTitle>
          <DialogDescription className="text-xs">It will be created with all existing options (sizes, surfaces, units). Remove the ones that don't apply afterwards.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-sm font-bold">Category name</label>
            <Input data-testid="new-category-input" value={label} onChange={(e) => setLabel(e.target.value)} onKeyDown={(e) => e.key === "Enter" && submit()} placeholder="e.g. Natural Stones" className="h-10" autoFocus />
          </div>
          <Button data-testid="new-category-submit" onClick={submit} disabled={busy} className="w-full font-bold h-11">{busy ? "Creating…" : "Create Category"}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
